import React, {useCallback, useEffect, useMemo} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useTranslation} from 'react-i18next';
import {useFormikContext} from 'formik';
import {Grid, makeStyles} from '@material-ui/core';
import {Colors} from '../../../../styles/Colors';
import {ReduxState} from '../../../../store/types';
import {actions} from '../../../../store';
import TextField from '../../../../components/TextField/TextField';
import SelectField from '../../../../components/SelectField/SelectField';
import IconWithTooltip from '../../../../components/Tooltip/IconWithTooltip';
import KeyboardDateTimePicker from '../../../../components/KeyboardDateTimePicker/KeyboardDateTimePicker';
import {isAnyKeyFromSearchExistInSource} from '../../../../utils/isAnyKeyFromSearchExistInSource';
import {accountEmailMaxLength, accountIdMaxLength} from '../../AddNewAccountDialog/AddNewAccountDialog.utils';
import {AccountIdFaultKind, getAccountFaultKind} from '../../../../store/types/Account';
import dayjs from '../../../../services/customDayJs';

const useStyles = makeStyles(() => ({
    inputs: {
        display: 'flex',
        flexDirection: 'column',
        flexGrow: 1,
        alignItems: 'stretch',
        '& .MuiFormControl-root': {
            flex: 1,
        },
    },
    itemsContainer: {
        padding: '32px 0px',
        maxWidth: 944,
        display: 'flex',
        flexDirection: 'column',
    },
    topContainer: {
        paddingTop: 24,
    },
    rowContainer: {
        flex: 1,
        display: 'flex',
        alignItems: 'flex-start',
        padding: '0px 16px 24px 16px',

        '& > :first-child': {
            marginRight: 32,
        },
        '&:last-of-type': {
            paddingBottom: 0,
        },
    },
    separator: {
        height: 1,
        width: 944,
        backgroundColor: Colors.Border,
    },
    sectionHeaderContainer: {
        padding: '0px 24px 0px 24px',
        alignItems: 'center',
        display: 'flex',
        marginBottom: 16,
        marginTop: -28,
    },
    sectionHeader: {
        color: Colors.Text,
        fontWeight: 700,
        marginRight: 12,
    },
}));

export type GeneralTabFormType = {
    id: string;
    email: string;
    time_zone_name: string;
    country: string;
    city: string;
    baddr1: string;
    zip: string;
    state: string;
    activation_date: string | null;
};

export const generalTabValidationReqFieldsName: string[] = ['id'];

interface GeneralTabFormProps {
    handleDirtyChange?: (funcName: string, isDirty: boolean) => void;
    handleSetSubmitFunc?: (funcName: string, func: () => void) => void;
    handleSetIsValidFunc?: (funcName: string, func: () => boolean) => void;
    tabName?: string;
    tabIndex?: number;
}

const GeneralTabForm: React.VFC<GeneralTabFormProps> = ({
    handleDirtyChange,
    handleSetSubmitFunc,
    handleSetIsValidFunc,
    tabName,
    tabIndex,
}) => {
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const classes = useStyles();

    const accountDetails = useSelector((state: ReduxState) => state.accounts.accountDetails);
    const editErrors = useSelector((state: ReduxState) => state.accounts.editErrors);
    const timeZonesList = useSelector((state: ReduxState) => state.generic.timeZonesList ?? []);
    const countriesList = useSelector((state: ReduxState) => state.generic.countriesList ?? []);
    const subdivisionsList = useSelector((state: ReduxState) => state.generic.subdivisionsList ?? []);
    const userDateFormat = useSelector<ReduxState, string>(
        (state) =>
            state.generic.globalCustomerInfo?.customer_info?.out_date_format || '',
    );

    const countriesOptionsList = useMemo(
        () => [{iso_3166_1_a2: '', name: ''}, ...countriesList],
        [countriesList],
    );

    const getSubdivisions = useCallback((iso_3166_1_a2?: string) => {
        if (iso_3166_1_a2) {
            dispatch(actions.getSubdivisionData.request({iso_3166_1_a2}));
        }
    }, []);

    const {
        values,
        handleChange,
        handleSubmit,
        setFieldValue,
        setFieldError,
        dirty,
        errors,
        touched,
        isValid,
    } = useFormikContext<GeneralTabFormType>();

    const markTabAsHaveError = (setTabError: boolean) => {
        if (tabName && tabIndex !== undefined) {
            dispatch(actions.setErrorInTab({name: tabName, index: tabIndex, markAsInvalid: setTabError}));
        }
    };

    useEffect(() => {
        const errorsKeys = Object.keys(errors);
        if (isAnyKeyFromSearchExistInSource(errorsKeys, generalTabValidationReqFieldsName)) {
            markTabAsHaveError(true);
        } else {
            markTabAsHaveError(false);
        }
    }, [errors, values]);

    useEffect(() => {
        if (accountDetails?.country) {
            getSubdivisions(accountDetails.country);
        }
    }, [accountDetails?.country]);

    useEffect(() => {
        handleDirtyChange?.('general', dirty);
    }, [dirty]);

    useEffect(() => {
        handleSetIsValidFunc?.('general', () => isValid);
    }, [isValid]);

    useEffect(() => {
        handleSetSubmitFunc?.('general', handleSubmit);
    }, [handleSubmit]);

    useEffect(() => {
        if (!editErrors) return;

        switch (getAccountFaultKind(editErrors)) {
            case AccountIdFaultKind.DuplicateId:
                return setFieldError('id', t('errors:accounts.duplicateId'));
            case AccountIdFaultKind.ForbiddenCharacter:
                return setFieldError('id', t('errors:portalUsers.portalRegex'));
        }
    }, [editErrors]);

    useEffect(() => {
        return () => {
            dispatch(actions.clearEditAccountErrors());
        };
    }, []);

    return (
        <div className={classes.inputs}>
            <Grid
                item
                className={`${classes.itemsContainer} ${classes.topContainer}`}
            >
                <div className={classes.rowContainer}>
                    <TextField
                        id="id"
                        label={t('screens:accounts.accountId')}
                        value={values.id}
                        onChange={handleChange}
                        setFieldError={setFieldError}
                        required
                        maxLength={accountIdMaxLength}
                        helperText={touched.id || !!errors.id ? errors.id : undefined}
                        dataQa="account-details-id"
                        icon={
                            <IconWithTooltip
                                dataQa="account-id-tooltip"
                                tooltipText={t('tooltips:accounts.accountId')}
                                copy={false}
                            />
                        }
                        iconPosition="end"
                    />
                    <KeyboardDateTimePicker
                        label={t('screens:accounts.activationDate')}
                        value={values.activation_date}
                        onChange={(val) => setFieldValue('activation_date', val ?? null)}
                        withHours={false}
                        disablePast
                        minDate={dayjs().toDate()}
                        dataQa="account-details-activation-date"
                        userDateTimeFormat={userDateFormat}
                        disabled={!!values.activation_date && !dayjs(values.activation_date).isAfter(dayjs(), 'day')}
                        icon={
                            <IconWithTooltip
                                dataQa="account-activation-date-tooltip"
                                tooltipText={t('tooltips:accounts.activationDate')}
                                copy={false}
                            />
                        }
                    />
                </div>

                <div className={classes.rowContainer}>
                    <TextField
                        id="email"
                        label={t('screens:accounts.email')}
                        value={values.email}
                        onChange={handleChange}
                        maxLength={accountEmailMaxLength}
                        helperText={touched.email ? errors.email : undefined}
                        dataQa="account-details-email"
                        icon={
                            <IconWithTooltip
                                dataQa="account-email-tooltip"
                                tooltipText={t('tooltips:accounts.email')}
                                copy={false}
                            />
                        }
                        iconPosition="end"
                    />

                    <SelectField
                        id="time_zone_name"
                        label={t('screens:accounts.timezone')}
                        items={timeZonesList.map((tz: {time_zone_name: string}) => tz.time_zone_name)}
                        value={values.time_zone_name || null}
                        onChange={(_, value) => setFieldValue('time_zone_name', value ?? '')}
                        dataQa="account-details-timezone"
                    />
                </div>
            </Grid>

            <Grid item className={classes.itemsContainer}>
                <div className={classes.sectionHeaderContainer}>
                    <span className={classes.sectionHeader}>
                        {t('screens:accounts.address')}
                    </span>
                    <IconWithTooltip
                        dataQa="account-address-tooltip"
                        tooltipText={t('tooltips:accounts.address')}
                        copy={false}
                    />
                </div>

                <div className={classes.rowContainer}>
                    <SelectField
                        label={t('screens:accounts.country')}
                        items={countriesOptionsList.map((v) => v.name)}
                        value={
                            countriesOptionsList.find(
                                (v) => v.iso_3166_1_a2 === values.country,
                            )?.name || null
                        }
                        onChange={(_, value) => {
                            const country = countriesOptionsList.find(
                                (v) => v.name === value,
                            )?.iso_3166_1_a2;
                            setFieldValue('country', country || '', false);
                            setFieldValue('state', '');
                            getSubdivisions(country);
                        }}
                        dataQa="account-details-country"
                    />
                    <TextField
                        id="city"
                        label={t('screens:accounts.city')}
                        value={values.city}
                        onChange={handleChange}
                        dataQa="account-details-city"
                    />
                </div>

                <div className={classes.rowContainer}>
                    <TextField
                        id="baddr1"
                        label={t('screens:accounts.addressLine')}
                        value={values.baddr1}
                        onChange={handleChange}
                        dataQa="account-details-address"
                    />
                    <TextField
                        id="zip"
                        label={t('screens:accounts.postalCode')}
                        value={values.zip}
                        onChange={handleChange}
                        dataQa="account-details-zip"
                    />
                </div>

                <div className={classes.rowContainer}>
                    <SelectField
                        key={`subdivision-${subdivisionsList.length}`}
                        label={t('screens:accounts.provinceState')}
                        items={subdivisionsList.map((v) => v.name)}
                        value={values.state || null}
                        onChange={(_, value) => {
                            setFieldValue('state', value || null);
                        }}
                        dataQa="account-details-subdivision"
                        disabled={!values.country}
                    />
                    <div style={{flex: 1}} />
                </div>
            </Grid>
        </div>
    );
};

export default GeneralTabForm;